import { useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useQuery } from "@tanstack/react-query";

import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import {
  PortalAlert,
  PortalButton,
  PortalCard,
  PortalLoading,
  PortalStatusChip,
} from "components/portal/PortalUi";
import MDTypography from "components/MDTypography";
import { useAuth } from "context/AuthContext";
import { get } from "lib/api";
import { isCustomerRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";
import { coreReturnLifecycleLabel } from "lib/coreReturnLifecycle";

function formatMoney(value, currency) {
  const amount = Number(value || 0);
  const code = String(currency || "USD").toUpperCase();
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
    }).format(amount);
  } catch {
    return `${code} ${amount.toFixed(2)}`;
  }
}

function formatDateTime(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
}

export default function CustomerCoreReturns() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useStorefrontCopy();
  const role = String(pluginRole || "").toLowerCase();
  const isCustomer = isCustomerRole(role);

  const query = useQuery({
    queryKey: ["storefront-valve-bodies", "core-returns-hub"],
    queryFn: () => get("/api/storefront/valve-bodies/me"),
    enabled: !!token && isCustomer,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
  const meQuery = useQuery({
    queryKey: ["storefront-me", "capabilities", "core-returns-hub"],
    queryFn: () => get("/api/storefront/me"),
    enabled: !!token && isCustomer,
    staleTime: 15000,
  });

  const canSubmitCore = meQuery.data?.capabilities?.submitCoreReturn !== false;
  const rows = useMemo(() => {
    const all = Array.isArray(query.data?.data) ? query.data.data : [];
    return all.filter((row) => {
      const lifecycle = row?.lifecycle || {};
      const outstanding = Number(
        lifecycle?.refundableCoreOutstandingQty ??
          lifecycle?.coreObligation?.outstandingQty ??
          0
      );
      // Match staff ERP: only invoice-backed owed cores (plus completed history).
      return (lifecycle?.coreDue && outstanding > 0) || lifecycle?.coreReturned;
    });
  }, [query.data]);

  const owedRows = rows.filter((row) => row?.lifecycle?.coreDue);
  const activeRows = rows.filter(
    (row) =>
      row?.lifecycle?.coreDue &&
      row?.lifecycle?.activeReturnRequest &&
      !row?.lifecycle?.coreReturned
  );

  if (!token) {
    return (
      <Navigate
        to="/authentication/sign-in"
        state={{ from: location }}
        replace
      />
    );
  }
  if (authLoading || pluginRole == null) return null;
  if (!isCustomer) return <Navigate to="/dashboard" replace />;

  return (
    <CustomerPortalLayout
      title={t("coreReturn.hubTitle", "Core returns")}
      subtitle={t(
        "coreReturn.hubSubtitle",
        "Return owed cores with a prepaid label in three easy steps."
      )}
      actions={
        <PortalButton
          variant="ghost"
          startIcon="qr_code_2"
          onClick={() => navigate("/account/valve-bodies")}
        >
          {t("portal.registeredUnits", "Registered Items")}
        </PortalButton>
      }
    >
      {query.error ? (
        <PortalAlert severity="error">
          {query.error?.response?.data?.error?.message ||
            query.error?.message ||
            t("coreReturn.loadError", "Failed to load core returns.")}
        </PortalAlert>
      ) : null}
      {!canSubmitCore ? (
        <PortalAlert severity="info" sx={{ mb: 2 }}>
          {t(
            "portal.viewOnlyNotice",
            "Some actions are hidden because your membership is view-only."
          )}
        </PortalAlert>
      ) : null}

      {query.isLoading ? (
        <PortalLoading label={t("coreReturn.loading", "Loading core returns...")} />
      ) : !rows.length ? (
        <PortalAlert severity="info">
          {t(
            "coreReturn.hubEmpty",
            "You do not have any cores owed or active core returns right now."
          )}
        </PortalAlert>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDTypography
              variant="overline"
              sx={{ opacity: 0.7, letterSpacing: "0.08em" }}
            >
              {t("coreReturn.owedSection", "Cores you owe")} ({owedRows.length})
            </MDTypography>
          </Grid>
          {owedRows.length ? (
            owedRows.map((row) => {
              const lifecycle = row?.lifecycle || {};
              const id = row?.documentId;
              return (
                <Grid item xs={12} md={6} key={`owed-${id}`}>
                  <PortalCard
                    accentTop
                    title={
                      row?.partNumber ||
                      row?.displayTag ||
                      t("registeredUnits.fallbackName", "Registered Item")
                    }
                    subtitle={
                      row?.tag
                        ? `Tag ${row.tag}`
                        : row?.serialNumber ||
                          t("coreReturn.serialPending", "Serial pending")
                    }
                    action={
                      <PortalStatusChip
                        label={coreReturnLifecycleLabel(lifecycle, t)}
                        tone="accent"
                      />
                    }
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 1,
                        mb: 2,
                      }}
                    >
                      <Box>
                        <MDTypography
                          variant="caption"
                          color="text"
                          sx={{ opacity: 0.7 }}
                        >
                          {t("coreReturn.creditLabel", "Refundable credit")}
                        </MDTypography>
                        <MDTypography variant="button" fontWeight={700}>
                          {formatMoney(
                            lifecycle?.refundableCoreAmount,
                            lifecycle?.currency
                          )}
                        </MDTypography>
                      </Box>
                      <Box>
                        <MDTypography
                          variant="caption"
                          color="text"
                          sx={{ opacity: 0.7 }}
                        >
                          {t("coreReturn.deadlineLabel", "Deadline")}
                        </MDTypography>
                        <MDTypography variant="button" fontWeight={700}>
                          {formatDateTime(lifecycle?.coreDueAt)}
                        </MDTypography>
                      </Box>
                    </Box>
                    <PortalButton
                      variant="primary"
                      disabled={!canSubmitCore}
                      onClick={() =>
                        navigate(`/account/valve-bodies/${id}/core-return`)
                      }
                    >
                      {t("portal.returnCore", "Return core")}
                    </PortalButton>
                  </PortalCard>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <PortalAlert severity="info">
                {t("coreReturn.noOwed", "No cores are currently owed.")}
              </PortalAlert>
            </Grid>
          )}

          {activeRows.length ? (
            <>
              <Grid item xs={12} sx={{ mt: 1 }}>
                <MDTypography
                  variant="overline"
                  sx={{ opacity: 0.7, letterSpacing: "0.08em" }}
                >
                  {t("coreReturn.activeSection", "In progress")} (
                  {activeRows.length})
                </MDTypography>
              </Grid>
              {activeRows.map((row) => {
                const lifecycle = row?.lifecycle || {};
                const id = row?.documentId;
                const request = lifecycle?.activeReturnRequest || {};
                return (
                  <Grid item xs={12} md={6} key={`active-${id}`}>
                    <PortalCard
                      accentTop
                      title={
                        row?.partNumber ||
                        row?.displayTag ||
                        t("registeredUnits.fallbackName", "Registered Item")
                      }
                      subtitle={
                        [
                          request?.shippingCarrier,
                          request?.trackingNumber,
                        ]
                          .filter(Boolean)
                          .join(" • ") ||
                        t("coreReturn.awaitingLabel", "Awaiting label")
                      }
                      action={
                        <PortalStatusChip
                          label={coreReturnLifecycleLabel(lifecycle, t)}
                          tone="neutral"
                        />
                      }
                    >
                      <PortalButton
                        variant="ghost"
                        onClick={() =>
                          navigate(`/account/valve-bodies/${id}/core-return`)
                        }
                      >
                        {t("coreReturn.openReturn", "Open return")}
                      </PortalButton>
                    </PortalCard>
                  </Grid>
                );
              })}
            </>
          ) : null}
        </Grid>
      )}
    </CustomerPortalLayout>
  );
}
